import { AddIcon, Button, Grid, GridRow } from '@bp/ui-components';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { PersonRole, use_PreviewTeachingLoadQuery } from '../../../../types/planung-graphql-client-defs';
import { partition } from '../../../../utils/arrayFunc';
import { LessonFormTeacherType, LessonFormType } from '../../types';
import { LessonTeacherFormItem, lessonTeacherSortableId } from './LessonTeacherFormItem';

type LessonTeacherFormProps = { disabled: boolean; defaults?: LessonFormTeacherType };

export const LessonTeacherForm = observer(({ disabled, defaults }: LessonTeacherFormProps) => {
  const { t } = useTranslation();

  const { values, setFieldTouched } = useFormikContext<LessonFormType>();

  const { teacherData } = useLoadBasicData({ pause: false });

  const teachers = teacherData?.people.filter((person) => person.role === PersonRole.Teacher);

  const [qualifiedTeachers, other] = partition(
    teachers ?? [],
    (person) => !!person.qualifications.find((quali) => quali.subjectUuid === values.subject?.uuid),
  );

  // Die Array-Position ist die Reihenfolge: buildTeachersConnect schreibt sie als edge.order.
  async function handleDragEnd({ active, over }: DragEndEvent, arrayHelpers: FieldArrayRenderProps) {
    if (!over || active.id === over.id) return;

    const ids = (values.teachers ?? []).map(lessonTeacherSortableId);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));

    if (oldIndex === -1 || newIndex === -1) return;

    arrayHelpers.move(oldIndex, newIndex);
    await setFieldTouched('teachers', true);
  }

  return (
    <FieldArray name={'teachers'}>
      {(arrayHelpers) => {
        return (
          <Grid useFormGap>
            <DndContext
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleDragEnd(event, arrayHelpers)}
            >
              <SortableContext
                strategy={verticalListSortingStrategy}
                items={(values.teachers ?? []).map(lessonTeacherSortableId)}
              >
                {values.teachers?.map((teacher, index) => {
                  teacher.teachingLoadHoursDisplay = (
                    values.teachingLoadInfo?.deputatByTeacher.get(teacher.person?.uuid ?? '') ?? 0
                  ).toLocaleString('de');
                  return (
                    <LessonTeacherFormItem
                      key={lessonTeacherSortableId(teacher, index)}
                      disabled={disabled}
                      index={index}
                      qualifiedTeachers={qualifiedTeachers}
                      otherTeachers={other}
                      teacher={teacher}
                      onDelete={() => arrayHelpers.remove(index)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>

            <GridRow spacingBottom='none' spacingTop='none'>
              <Button
                hierarchy='tertiary'
                icon={<AddIcon />}
                disabled={!values.subject || disabled}
                onClick={async () => {
                  if (values.teachers && values.teachers.length > 0) {
                    for (const index in values.teachers) {
                      await setFieldTouched(`teachers.${index}`);
                    }
                  }
                  arrayHelpers.push({ person: { uuid: v4() }, ...defaults });
                }}
              >
                {t('persons.addTeacher')}
              </Button>
            </GridRow>
          </Grid>
        );
      }}
    </FieldArray>
  );
});
